import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorHandleService } from './error-handle.service';
import { BehaviorSubject, catchError, map, Observable, of, tap, throwError } from 'rxjs';
import { LoginRequest, LoginResponse, User } from '../models/user';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment.development';
import { UrlValidatorService } from './url-validator.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private apiUrl = `${environment.APIDEV}`;
  private currentUserSubject: BehaviorSubject<User | null>;
  public currentUser: Observable<User | null>;

  constructor(
    private http: HttpClient,
    private errorHandle: ErrorHandleService,
    private router: Router,
    private urlValidator: UrlValidatorService,
  ) {
    const storedUser = localStorage.getItem('currentUser');
    this.currentUserSubject = new BehaviorSubject<User | null>(
      storedUser ? JSON.parse(storedUser) : null
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  login(loginData: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${this.apiUrl}login`,
      loginData,
      this.errorHandle._httpOptions()
    ).pipe(
      map(response => {
        // Check if the response is successful
        if (response.success && response.data) {
          // Store user details and token in local storage
          localStorage.setItem('token', response.data.access_token);
          localStorage.setItem('currentUser', JSON.stringify({
            name: response.data.name,
            email: response.data.email,
            picture: response.data.picture,
            roles: response.data.roles,
            permissions: response.data.permissions,
            business_lines: response.data.business_lines || [] // Pastikan business_lines selalu ada
          }));
          this.currentUserSubject.next(response.data);
        }
        // Return response regardless of success status
        return response;
      }),
      catchError(error => {
        // Perbaikan: error.status adalah HTTP status code, bukan boolean
        if (error.status === 400 || error.status === 401) {
          return throwError(() => ({
            success: false,
            message: error.error?.message,
            meta: null,
            data: undefined  // gunakan undefined alih-alih null
          } as LoginResponse));
        } else if (error.status === 500) {
          return throwError(() => ({
            success: false,
            message: error.error?.message,
            meta: null,
            data: undefined  // gunakan undefined alih-alih null
          } as LoginResponse));
        }
        return throwError(() => error);
      })
    );
  }

  logout(token: string, redirectUrl?: string): Observable<any> {
    // Buat header dengan token
    const headers = {
      'Authorization': `Bearer ${token}`
    };

    // Lakukan request logout ke API dengan metode POST
    return this.http.post<{ success: boolean, message: string, meta: any, data: any }>(
      `${this.apiUrl}logout`,
      {}, // body kosong
      { headers } // headers dengan token
    ).pipe(
      tap(response => {
        // Jika response success, hapus token dan currentUser dari localStorage
        if (response && response.success === true) {
          localStorage.removeItem('token');
          localStorage.removeItem('currentUser');

          // Reset current user subject
          this.currentUserSubject.next(null);

          // Jika ada redirectUrl, validasi dan navigate
          if (redirectUrl) {
            const safeUrl = this.urlValidator.validateReturnUrl(redirectUrl);
            this.router.navigate([safeUrl]);
          }
        }
      })
    );
  }

  isLoggedIn(): boolean {
    return !!this.currentUserValue;
  }

  // Check if token is still valid (you might want to add token expiration logic)
  checkAuthStatus(): Observable<boolean> {
    const user = this.currentUserValue;
    if (user) {
      // For now, just checking if the user exists in localStorage
      return of(true);
    }
    return of(false);
  }
}
